import Select from '@xver/components/Atoms/Select/Select';
import { IconDoorExit, IconTrash, IconTransfer } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { ApiError, ProjectDetailModel } from '@xver/apis/v1/projects/model';
import { Button, ConfirmActionModal, TypeToConfirmModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useActiveProjectScope } from '@xver/hooks/useActiveProject';
import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';
import {
    invalidateProjectQueries,
    PROJECT_DETAIL_QUERY_KEY,
    useProjectFeedback,
    useProjectMembersQuery,
} from '@xver/hooks/useProjectQueries';
import { useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import { setActiveProject } from '@xver/store/project/project.actions';
import { formatDateTime } from '@xver/utils/date';
import { getErrorCode } from '@xver/utils/error';
import {
    focusAfterRender,
    memberOptionLabel,
    remainingSummary,
    SELECT_SEARCHABLE_AFTER,
    transferKeepsPayer,
    transferNotices,
} from '@xver/utils/projects';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ResourceSectionKey } from './ResourcesTabPanel';

interface DangerZoneTabPanelProps {
    project: ProjectDetailModel;
    onShowResources: (section: ResourceSectionKey) => void;
}

function RemainingResources({
    remaining,
    onShow,
}: {
    remaining: ReturnType<typeof remainingSummary>;
    onShow: (section: ResourceSectionKey) => void;
}) {
    const { t } = useTranslation('projects');
    return (
        <ul className="flex flex-col gap-2">
            {remaining.map(({ key, count }) => (
                <li key={key} className="flex flex-wrap items-center gap-2">
                    <span>{t(`counts.${key}`, { count })}</span>
                    <Button size="lg" variant="secondary" onClick={() => onShow(key)}>
                        {t(`danger.delete.show.${key}`)}
                    </Button>
                </li>
            ))}
        </ul>
    );
}

/** Leaves `/projects/:id` for the list, first moving the active project off `projectId`. */
function useExitProject(projectId: string) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId } = useActiveProjectScope();
    const { data: projects } = useProjectsQuery();

    return async () => {
        if (userId !== undefined && activeProjectId === projectId) {
            const fallback = projects?.find(
                (candidate) => candidate.isDefault && candidate.role === 'owner'
            );
            if (fallback) dispatch(setActiveProject({ userId, projectId: fallback.id }));
        }
        navigate('/projects');
        queryClient.removeQueries({
            predicate: ({ queryKey }) =>
                queryKey[1] === projectId && String(queryKey[0]).startsWith('apis.v1.projects.'),
        });
        await invalidateProjectQueries(queryClient);
    };
}

export default function DangerZoneTabPanel({ project, onShowResources }: DangerZoneTabPanelProps) {
    const { t, i18n } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess, notifyError } = useProjectFeedback();
    const exitProject = useExitProject(project.id);
    const isOwner = project.role === 'owner';
    const members = useProjectMembersQuery(project.id);
    const [transferOpen, setTransferOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [recipient, setRecipient] = useState('');
    const [recipientError, setRecipientError] = useState<string>();
    const recipientRef = useRef<HTMLInputElement>(null);
    const defaultLockedId = useId();
    const notEmptyId = useId();
    const transferButtonId = useId();
    const cancelTransferButtonId = useId();
    const notices = transferNotices(project.resources);

    const offer = useMutation({
        mutationFn: () =>
            Endpoints.v1.Projects.offerTransfer(project.id, {
                userId: Number(recipient),
                confirmName: project.name,
            }),
        onSuccess: async (updated) => {
            setTransferOpen(false);
            notifySuccess(
                t('danger.transfer.offeredTitle'),
                t('danger.transfer.offeredMessage', { name: updated.ownershipOffer?.toName ?? '' })
            );
            await invalidateProjectQueries(queryClient);
            focusAfterRender(() => document.getElementById(cancelTransferButtonId));
        },
    });

    const cancelOffer = useMutation({
        mutationFn: () => Endpoints.v1.Projects.cancelTransfer(project.id),
        onSuccess: async () => {
            notifySuccess(
                t('danger.transfer.cancelledTitle'),
                t('danger.transfer.cancelledMessage')
            );
            await invalidateProjectQueries(queryClient);
            focusAfterRender(() => document.getElementById(transferButtonId));
        },
        onError: notifyError,
    });

    const remove = useMutation({
        mutationFn: () => Endpoints.v1.Projects.deleteProject(project.id),
        onSuccess: async () => {
            setDeleteOpen(false);
            notifySuccess(
                t('danger.delete.doneTitle'),
                t('danger.delete.doneMessage', { name: project.name })
            );
            await exitProject();
        },
    });

    const leave = useMutation({
        mutationFn: () => Endpoints.v1.Projects.leaveProject(project.id),
        onSuccess: async () => {
            setLeaveOpen(false);
            notifySuccess(
                t('danger.leave.doneTitle'),
                t('danger.leave.doneMessage', { name: project.name })
            );
            await exitProject();
        },
        onError: notifyError,
    });

    const candidates = (members.data ?? []).filter((member) => member.role !== 'owner');
    const remaining =
        getErrorCode(remove.error) === 'project_not_empty'
            ? remainingSummary((remove.error as unknown as ApiError).remaining)
            : [];
    const knownRemaining = remainingSummary(project.resources);

    useEffect(() => {
        void queryClient.invalidateQueries({ queryKey: [...PROJECT_DETAIL_QUERY_KEY, project.id] });
    }, [queryClient, project.id]);

    const openTransfer = () => {
        offer.reset();
        setRecipient('');
        setRecipientError(undefined);
        setTransferOpen(true);
    };

    if (!isOwner) {
        return (
            <div className="w-full flex flex-col gap-6">
                <DialogBox label={t('danger.leave.title')}>
                    <div className="w-full p-4 flex flex-col items-start gap-3 text-sm">
                        <p className="max-w-[80ch] text-fg/78">{t('danger.leave.description')}</p>
                        <Button
                            size="lg"
                            variant="danger"
                            leftIcon={<IconDoorExit size={16} aria-hidden="true" />}
                            onClick={() => setLeaveOpen(true)}
                        >
                            {t('danger.leave.open')}
                        </Button>
                    </div>
                </DialogBox>
                <ConfirmActionModal
                    opened={leaveOpen}
                    onClose={() => {
                        if (!leave.isPending) setLeaveOpen(false);
                    }}
                    title={t('danger.leave.confirmTitle', { name: project.name })}
                    message={t('danger.leave.confirmMessage')}
                    confirmLabel={t('danger.leave.confirm')}
                    confirmVariant="danger"
                    isLoading={leave.isPending}
                    onConfirm={() => leave.mutate()}
                />
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-6">
            <DialogBox label={t('danger.transfer.title')}>
                <div className="w-full p-4 flex flex-col items-start gap-3 text-sm">
                    {project.isDefault ? (
                        <p className="max-w-[80ch] text-fg/78">
                            {t('danger.transfer.defaultLocked')}
                        </p>
                    ) : project.ownershipOffer ? (
                        <>
                            <p className="max-w-[80ch]">
                                {t('danger.transfer.pending', {
                                    name: project.ownershipOffer.toName,
                                    date: formatDateTime(
                                        project.ownershipOffer.expiresAt,
                                        i18n.language
                                    ),
                                })}
                            </p>
                            <Button
                                id={cancelTransferButtonId}
                                size="lg"
                                variant="secondary"
                                onClick={() => cancelOffer.mutate()}
                                isLoading={cancelOffer.isPending}
                            >
                                {t('danger.transfer.cancel')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <p className="max-w-[80ch] text-fg/78">
                                {t('danger.transfer.description')}
                            </p>
                            <Button
                                id={transferButtonId}
                                size="lg"
                                variant="secondary"
                                leftIcon={<IconTransfer size={16} aria-hidden="true" />}
                                onClick={openTransfer}
                            >
                                {t('danger.transfer.open')}
                            </Button>
                        </>
                    )}
                </div>
            </DialogBox>

            <DialogBox label={t('danger.delete.title')}>
                <div className="w-full p-4 flex flex-col items-start gap-3 text-sm">
                    {project.isDefault ? (
                        <>
                            <p id={defaultLockedId} className="max-w-[80ch] text-fg/78">
                                {t('danger.delete.defaultLocked')}
                            </p>
                            <Button
                                size="lg"
                                variant="danger"
                                aria-disabled="true"
                                aria-describedby={defaultLockedId}
                                leftIcon={<IconTrash size={16} aria-hidden="true" />}
                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                                {t('danger.delete.open')}
                            </Button>
                        </>
                    ) : knownRemaining.length > 0 ? (
                        <>
                            <p className="max-w-[80ch] text-fg/78">
                                {t('danger.delete.description')}
                            </p>
                            <p id={notEmptyId} className="max-w-[80ch]">
                                {t('danger.delete.notEmpty')}
                            </p>
                            <RemainingResources
                                remaining={knownRemaining}
                                onShow={onShowResources}
                            />
                            <Button
                                size="lg"
                                variant="danger"
                                aria-disabled="true"
                                aria-describedby={notEmptyId}
                                leftIcon={<IconTrash size={16} aria-hidden="true" />}
                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                            >
                                {t('danger.delete.open')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <p className="max-w-[80ch] text-fg/78">
                                {t('danger.delete.description')}
                            </p>
                            <Button
                                size="lg"
                                variant="danger"
                                leftIcon={<IconTrash size={16} aria-hidden="true" />}
                                onClick={() => {
                                    remove.reset();
                                    setDeleteOpen(true);
                                }}
                            >
                                {t('danger.delete.open')}
                            </Button>
                        </>
                    )}
                </div>
            </DialogBox>

            <TypeToConfirmModal
                opened={transferOpen}
                onClose={() => setTransferOpen(false)}
                title={t('danger.transfer.confirmTitle', { name: project.name })}
                expected={project.name}
                inputLabel={t('danger.confirmLabel')}
                inputDescription={t('danger.confirmDescription', { name: project.name })}
                confirmLabel={t('danger.transfer.confirm')}
                confirmVariant="primary"
                isSubmitting={offer.isPending}
                validate={() => {
                    if (recipient) return true;
                    setRecipientError(t('danger.transfer.chooseMember'));
                    recipientRef.current?.focus();
                    return false;
                }}
                error={offer.isError ? errorMessage(offer.error) : undefined}
                onConfirm={() => offer.mutate()}
            >
                <ul className="list-disc ps-5 text-sm text-fg/78 flex flex-col gap-1 max-w-[80ch]">
                    <li>
                        {transferKeepsPayer(project, Number(recipient) || undefined)
                            ? t('danger.transfer.effects.ownerPayerStays', {
                                  payer: project.payer.name ?? t('billing.someone'),
                              })
                            : t('danger.transfer.effects.owner')}
                    </li>
                    <li>{t('danger.transfer.effects.you')}</li>
                    {notices.privateIp && <li>{t('move.crossOwner.privateIp')}</li>}
                    {notices.containers && (
                        <>
                            <li>{t('danger.transfer.effects.registry')}</li>
                            <li>{t('move.crossOwner.credentials')}</li>
                        </>
                    )}
                    {notices.prepaid && <li>{t('move.crossOwner.prepaid')}</li>}
                    <li>{t('danger.transfer.effects.expiry')}</li>
                </ul>
                {members.isError ? (
                    <p role="alert" className="text-sm">
                        {errorMessage(members.error)}
                    </p>
                ) : candidates.length === 0 && !members.isPending ? (
                    <p className="text-sm text-fg">{t('danger.transfer.noCandidates')}</p>
                ) : (
                    <Select
                        ref={recipientRef}
                        data-autofocus={!members.isPending || undefined}
                        size="md"
                        label={t('danger.transfer.recipientLabel')}
                        description={t('danger.transfer.recipientDescription')}
                        placeholder={t('billing.choosePlaceholder')}
                        data={candidates.map((member) => ({
                            value: String(member.userId),
                            label: memberOptionLabel(member),
                        }))}
                        value={recipient || null}
                        onChange={(value) => {
                            setRecipient(value ?? '');
                            setRecipientError(undefined);
                        }}
                        allowDeselect={false}
                        searchable={candidates.length > SELECT_SEARCHABLE_AFTER}
                        nothingFoundMessage={t('switcher.nothingFound')}
                        disabled={members.isPending || offer.isPending}
                        error={recipientError}
                    />
                )}
            </TypeToConfirmModal>

            <TypeToConfirmModal
                opened={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                title={t('danger.delete.confirmTitle', { name: project.name })}
                expected={project.name}
                inputLabel={t('danger.confirmLabel')}
                inputDescription={t('danger.confirmDescription', { name: project.name })}
                confirmLabel={t('danger.delete.confirm')}
                confirmIcon={<IconTrash size={16} aria-hidden="true" />}
                isSubmitting={remove.isPending}
                error={
                    remove.isError ? (
                        <div className="flex flex-col gap-2">
                            <p>{errorMessage(remove.error)}</p>
                            {remaining.length > 0 && (
                                <RemainingResources
                                    remaining={remaining}
                                    onShow={(key) => {
                                        setDeleteOpen(false);
                                        onShowResources(key);
                                    }}
                                />
                            )}
                        </div>
                    ) : undefined
                }
                onConfirm={() => remove.mutate()}
            >
                <p className="text-sm text-fg/78 max-w-[80ch]">{t('danger.delete.consequence')}</p>
            </TypeToConfirmModal>
        </div>
    );
}
